import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import {
  CalendarDays, Loader2, Video, Phone, Globe, FileText, Check, Clock,
  Download, AlertTriangle, X, RefreshCw, HeartPulse,
} from "lucide-react";
import { ClientMedicalRecords } from "@/components/MedicalRecords";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  appointment_confirmed: "bg-emerald-50 text-emerald-600", forms_pending: "bg-amber-50 text-amber-600",
  forms_complete: "bg-emerald-50 text-emerald-600", visit_started: "bg-cobalt-light text-cobalt",
  visit_completed: "bg-slate-100 text-slate-500", client_no_show: "bg-red-50 text-red-600",
};

const local = (iso) => iso ? new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "—";

const SlotPicker = ({ requestId, onBooked, onClose, rescheduleId }) => {
  const [data, setData] = useState(undefined);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/client/telehealth/requests/${requestId}/slots`).then(({ data: d }) => setData(d)).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load times"); setData(null);
    });
  }, [requestId]);
  const book = async () => {
    setBusy(true);
    try {
      if (rescheduleId) await api.post(`/client/telehealth/appointments/${rescheduleId}/reschedule`, { start_utc: picked });
      else await api.post(`/client/telehealth/requests/${requestId}/book`, { start_utc: picked });
      toast.success("Appointment confirmed"); onBooked();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed"); }
    finally { setBusy(false); }
  };
  const byDay = {};
  (data?.slots || []).forEach((s) => {
    const day = new Date(s.start_utc).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    (byDay[day] = byDay[day] || []).push(s);
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="slot-picker">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-navy">{rescheduleId ? "Pick a new time" : "Choose your appointment time"}</h3>
          <button onClick={onClose} data-testid="slot-picker-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-xs text-slate-400 mt-1">Times are shown in your local time zone. The time is confirmed instantly once you select it.</p>
        {data === undefined ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
          !data || data.closed || !data.slots.length ? <div className="py-12 text-center text-sm text-slate-400" data-testid="slot-picker-empty">No open times right now. The provider's office will follow up, or check back soon.</div> : (
          <div className="mt-4 space-y-4">
            {Object.entries(byDay).slice(0, 7).map(([day, slots]) => (
              <div key={day}>
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">{day}</div>
                <div className="flex flex-wrap gap-2">
                  {slots.map((s) => (
                    <button key={s.start_utc} onClick={() => setPicked(s.start_utc)} data-testid={`slot-${s.start_utc}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${picked === s.start_utc ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {new Date(s.start_utc).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={book} disabled={!picked || busy} data-testid="slot-confirm"
              className="w-full py-3 rounded-full bg-cobalt text-white font-semibold text-sm disabled:opacity-40">
              {busy ? "Reserving…" : picked ? `Confirm ${local(picked)}` : "Select a time above"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const FormRow = ({ f, reload }) => {
  const [signing, setSigning] = useState(false);
  const [name, setName] = useState("");
  const complete = async () => {
    try {
      await api.post(`/client/telehealth/forms/${f.assignment_id}/complete`,
        name ? { signature_type: "typed", signature: name } : { signature_type: "acknowledgment" });
      toast.success(`${f.name} completed`); setSigning(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not complete form"); }
  };
  return (
    <div className="py-2 border-b border-slate-50 last:border-0" data-testid={`form-row-${f.assignment_id}`}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-1.5 text-navy"><FileText className="w-3.5 h-3.5 text-slate-400" /> {f.name} <span className="text-[10px] text-slate-400">v{f.version}</span></span>
        {f.status === "completed"
          ? <span className="flex items-center gap-1 text-emerald-600 text-xs font-semibold" data-testid={`form-done-${f.assignment_id}`}><Check className="w-3.5 h-3.5" /> Completed</span>
          : <button onClick={() => setSigning(!signing)} data-testid={`form-open-${f.assignment_id}`} className="px-3 py-1 rounded-full bg-amber-50 text-amber-600 text-xs font-semibold">Complete</button>}
      </div>
      {signing && f.status !== "completed" && (
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`form-signature-${f.assignment_id}`}
            className="flex-1 min-w-[180px] rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Type your full name to sign (optional for acknowledgment)" />
          <button onClick={complete} data-testid={`form-submit-${f.assignment_id}`} className="px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold">Sign & submit</button>
        </div>
      )}
    </div>
  );
};

export default function MyAppointments() {
  const [data, setData] = useState(undefined);
  const [booking, setBooking] = useState(null);
  const load = useCallback(() => {
    api.get("/client/telehealth/appointments").then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, []);
  useEffect(load, [load]);

  const cancel = async (id) => {
    const reason = window.prompt("Why are you cancelling? (optional)") || "Client cancelled";
    try { await api.post(`/client/telehealth/appointments/${id}/cancel`, { reason }); toast.success("Appointment cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not cancel"); }
  };

  const ics = async (id) => {
    try {
      const res = await api.get(`/client/telehealth/appointments/${id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `appointment_${id}.ics`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download calendar file"); }
  };

  return (
    <AppLayout>
      <div data-testid="my-appointments-page">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Telehealth</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">My Medical Appointments</h1>
        <div className="mt-4 rounded-2xl bg-red-50 border border-red-100 px-4 py-3 text-xs text-red-600 flex items-start gap-2" data-testid="emergency-notice">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          {data?.emergency_notice || "If this is a medical emergency, call 911 immediately. Telehealth coordination is not emergency care."}
        </div>

        {data === undefined ? <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div> :
          !data || !data.appointments.length ? (
            <div className="mt-8 py-14 text-center bg-white rounded-3xl border border-slate-100" data-testid="appointments-empty">
              <HeartPulse className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No telehealth coordination yet. If your attorney recommends a telehealth evaluation, it will appear here.</p>
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              {data.appointments.map((a) => {
                const appt = a.appointment;
                return (
                  <div key={a.request.request_id} className="bg-white rounded-3xl border border-slate-100 p-6" data-testid={`appointment-card-${a.request.request_id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-heading font-bold text-navy">{a.provider.practice_name || a.request.provider_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {(a.provider.provider_type || "").replace(/_/g, " ")} · Case {a.request.case_number}
                          {a.request.firm_name && ` · via ${a.request.firm_name}`}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                          {a.provider.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {a.provider.contact_phone}</span>}
                          {a.provider.website && <a href={a.provider.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-cobalt"><Globe className="w-3 h-3" /> Website</a>}
                          {a.provider.office_hours && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.provider.office_hours}</span>}
                        </div>
                      </div>
                      <span data-testid={`appointment-status-${a.request.request_id}`} className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${appt ? (STATUS_TONE[appt.status] || "bg-slate-100 text-slate-500") : "bg-cobalt-light text-cobalt"}`}>
                        {appt ? appt.status.replace(/_/g, " ") : a.request.status === "accepted" ? "ready to schedule" : a.request.status.replace(/_/g, " ")}
                      </span>
                    </div>

                    {a.can_schedule && (
                      <button onClick={() => setBooking({ requestId: a.request.request_id })} data-testid={`schedule-now-${a.request.request_id}`}
                        className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-semibold">
                        <CalendarDays className="w-4 h-4" /> Schedule my appointment
                      </button>
                    )}

                    {appt && (
                      <div className="mt-4 rounded-2xl bg-slate-50 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-navy flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-cobalt" /> {local(appt.start_utc)}</div>
                            <div className="text-[11px] text-slate-400 mt-0.5">{(appt.type || "").replace(/_/g, " ")} · virtual visit{appt.timezone ? ` · provider time zone ${appt.timezone}` : ""}</div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {appt.meeting_link ? (
                              <a href={appt.meeting_link} target="_blank" rel="noreferrer" data-testid={`join-visit-${appt.appointment_id}`}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold"><Video className="w-3.5 h-3.5" /> Join visit</a>
                            ) : <span className="text-[11px] text-slate-400 self-center">Secure meeting link will appear here before your visit.</span>}
                            <button onClick={() => ics(appt.appointment_id)} data-testid={`add-calendar-${appt.appointment_id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs"><Download className="w-3.5 h-3.5" /> Add to calendar</button>
                            {["appointment_confirmed", "forms_pending", "forms_complete"].includes(appt.status) && <>
                              <button onClick={() => setBooking({ requestId: a.request.request_id, rescheduleId: appt.appointment_id })} data-testid={`reschedule-${appt.appointment_id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs"><RefreshCw className="w-3.5 h-3.5" /> Reschedule</button>
                              <button onClick={() => cancel(appt.appointment_id)} data-testid={`cancel-${appt.appointment_id}`} className="px-3 py-2 rounded-full bg-white border border-slate-200 text-red-500 text-xs">Cancel</button>
                            </>}
                          </div>
                        </div>
                      </div>
                    )}

                    {(a.forms || []).length > 0 && (
                      <div className="mt-4" data-testid={`forms-section-${a.request.request_id}`}>
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Required forms {a.forms_complete ? "— all complete ✓" : `(${a.forms.filter((f) => f.status !== "completed").length} remaining)`}</div>
                        {a.forms.map((f) => <FormRow key={f.assignment_id} f={f} reload={load} />)}
                      </div>
                    )}

                    <ClientMedicalRecords caseId={a.request.case_id} />

                    {(a.communications || []).length > 0 && (
                      <details className="mt-4">
                        <summary className="text-xs font-semibold text-slate-500 cursor-pointer">Communication history</summary>
                        <div className="mt-2 divide-y divide-slate-50">
                          {a.communications.map((c, i) => (
                            <div key={i} className="py-1.5 text-[11px] flex justify-between gap-3">
                              <span className="text-navy">{c.title}</span>
                              <span className="text-slate-400 whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                );
              })}
              <p className="text-[11px] text-slate-400" data-testid="privacy-notice">{data.privacy_notice} Medical services are performed by independent licensed providers — Crash Ready coordinates scheduling and secure information flow only.</p>
            </div>
          )}
      </div>
      {booking && <SlotPicker requestId={booking.requestId} rescheduleId={booking.rescheduleId}
        onClose={() => setBooking(null)} onBooked={() => { setBooking(null); load(); }} />}
    </AppLayout>
  );
}
